import { useState, useEffect, useRef } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { IconButton, Tooltip } from "@similarweb/ui-components";

import { projectCreationDataAutomation } from "app/modules/project-from-comparison/dataAutomation";
import { useAccessForButton } from "app/modules/project-from-comparison/hooks/useAccessForButton";
import projectCreatingTrack from "app/modules/project-from-comparison/tracking";
import { useSessionStorage } from "app/pages/activation/hooks/helpers/useSessionStorage";

import { ALERT_BUTTON_ANIMATION_KEY, ANIMATION_START_DELAY } from "./constants";
import * as SC from "./styles";

interface IAlertButtonProps {
    onClick?: () => void;
}

export const AlertButton = ({ onClick }: IAlertButtonProps) => {
    const { translate } = useTranslate();
    const { isExist, hasFreeLimit, isWAPage, isProjectAllowed, hasAtLeast1domain } = useAccessForButton();
    const { getSessionStorage, setSessionStorage } = useSessionStorage();
    const [shouldShowAnimation, setShouldShowAnimation] = useState(false);
    const isHover = useRef(false);

    const shouldShowButton = isProjectAllowed && !isExist && isWAPage && hasFreeLimit && hasAtLeast1domain;

    const handleClick = () => {
        projectCreatingTrack.trackSaveProjectAlertsClick();
        onClick?.();
    };

    const handleMouseEnter = () => {
        if (isHover.current) {
            return;
        }

        isHover.current = true;
        projectCreatingTrack.trackSaveProjectAlertsHover();
    };

    const handleMouseLeave = () => {
        isHover.current = false;
    };

    useEffect(() => {
        if (!shouldShowButton) {
            return;
        }

        const hasSeenAnimation = getSessionStorage<boolean>(ALERT_BUTTON_ANIMATION_KEY);

        if (!hasSeenAnimation) {
            const delayTimer = setTimeout(() => {
                setShouldShowAnimation(true);
                setSessionStorage(ALERT_BUTTON_ANIMATION_KEY, true);
            }, ANIMATION_START_DELAY);

            return () => clearTimeout(delayTimer);
        }
    }, [shouldShowButton, getSessionStorage, setSessionStorage]);

    if (!shouldShowButton) {
        return null;
    }

    return (
        <Tooltip content={translate("activation.alerts.subscribe.button.text")}>
            <SC.Container
                $shouldShowAnimation={shouldShowAnimation}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <IconButton
                    onClick={handleClick}
                    iconName="alerts"
                    iconColor={colorsPalettes.blue[400]}
                    type="flat"
                    dataAutomation={projectCreationDataAutomation.alertsButton.cta}
                />
            </SC.Container>
        </Tooltip>
    );
};
